import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { AnalysisJob } from '@/types/scriptAnalysis';

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

export function useAnalysisProgress(jobId: string | null) {
  const [job, setJob] = useState<AnalysisJob | null>(null);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      return;
    }
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    const fetchJob = async () => {
      const { data } = await supabase
        .from('analysis_jobs')
        .select('*')
        .eq('id', jobId)
        .single();
      if (cancelled || !data) return;
      setJob(data as AnalysisJob);
      // Estado terminal alcanzado → dejamos de pollear.
      if (interval && TERMINAL.has((data as AnalysisJob).status)) {
        clearInterval(interval);
        interval = null;
      }
    };

    // Fetch inicial.
    fetchJob();

    // Realtime: actualizaciones instantáneas (cuando funcionan).
    const channel = supabase
      .channel(`job-${jobId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'analysis_jobs',
          filter: `id=eq.${jobId}`,
        },
        (payload) => {
          if (cancelled) return;
          const j = payload.new as AnalysisJob;
          setJob(j);
          if (interval && TERMINAL.has(j.status)) {
            clearInterval(interval);
            interval = null;
          }
        }
      )
      .subscribe();

    // POLLING DE RESPALDO (fiabilidad): la realtime a veces pierde el evento
    // final (completed/100%) y la UI se quedaba congelada (p.ej. en 89%). Un
    // sondeo cada 4s GARANTIZA que el estado final se refleje aunque realtime
    // falle. Se detiene solo al llegar a estado terminal.
    interval = setInterval(fetchJob, 4000);

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [jobId]);

  return job;
}
